import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Reveal, ImageReveal } from "@/components/Reveal";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    setActive(0);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        return api.get("/products", { params: { category: r.data.category, limit: 5 } });
      })
      .then((r) => setRelated((r?.data || []).filter((p) => p.id !== id).slice(0, 3)))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white pt-20">
        <p className="font-heading text-4xl font-bold uppercase">Product not found</p>
        <Link to="/products" className="text-gavra underline underline-offset-4">Back to catalogue</Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white pt-20">
        <span className="font-mono-hw text-xs uppercase tracking-[0.3em] text-black/40">Loading…</span>
      </div>
    );
  }

  const images = (product.gallery && product.gallery.length > 0)
    ? product.gallery
    : (product.image_url ? [product.image_url] : []);
  const mainImage = images[active] || product.image_url;

  return (
    <div data-testid="product-detail-page" className="min-h-screen bg-white pt-20">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10 py-10">
        <Link to="/products" className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.15em] text-black/50 hover:text-gavra">
          <ArrowLeft className="h-4 w-4" /> Back to catalogue
        </Link>

        <div className="mt-8 grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* GALLERY */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <ImageReveal className="relative aspect-square overflow-hidden border border-black/10 spotlight">
              {mainImage && (
                <img
                  key={mainImage}
                  data-testid="product-main-image"
                  src={mainImage}
                  alt={product.name}
                  className="h-full w-full object-cover"
                />
              )}
              <span className="absolute right-0 top-0 bg-black px-3 py-2 font-mono-hw text-[11px] font-bold uppercase tracking-wider text-white">
                {product.sku}
              </span>
            </ImageReveal>

            {images.length > 1 && (
              <div data-testid="product-thumbs" className="mt-4 grid grid-cols-5 gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`product-thumb-${i}`}
                    onClick={() => setActive(i)}
                    className={`aspect-square overflow-hidden border transition-colors ${
                      i === active ? "border-gavra" : "border-black/10 hover:border-black/40"
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* DETAILS */}
          <div>
            <Reveal>
              <p className="font-mono-hw text-[12px] uppercase tracking-[0.3em] text-gavra">
                {product.category}{product.series ? ` · ${product.series}` : ""}
              </p>
              <h1 data-testid="product-title" className="mt-4 font-heading text-4xl font-black uppercase leading-[0.9] tracking-tighter md:text-6xl">
                {product.name}
              </h1>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <span data-testid="sku-badge" className="bg-gavra px-3 py-1 font-mono-hw text-xs font-bold text-white">
                  SKU {product.sku}
                </span>
                <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${product.in_stock ? "text-black" : "text-black/40"}`}>
                  <Check className="h-4 w-4 text-gavra" /> {product.in_stock ? "Available" : "Currently unavailable"}
                </span>
              </div>

              {product.description && (
                <p className="mt-8 text-base leading-relaxed text-black/70 md:text-lg">{product.description}</p>
              )}

              {product.specs?.length > 0 && (
                <div className="mt-10 border-t border-black/10 pt-6">
                  <p className="font-heading text-lg font-bold uppercase tracking-tight">Specifications</p>
                  <dl className="mt-4 divide-y divide-black/10">
                    {product.specs.map((s, i) => (
                      <div key={i} className="flex items-center justify-between py-3">
                        <dt className="font-mono-hw text-[11px] uppercase tracking-widest text-black/50">{s.label}</dt>
                        <dd className="font-heading text-sm font-bold">{s.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <div className="mt-10 border-t border-black/10 pt-6">
                <p className="text-sm text-black/50">
                  This item is part of the GAVRA online catalog. To purchase, visit an authorized dealer.
                </p>
                <Link
                  to="/dealers"
                  data-testid="dealer-locator-btn"
                  className="mt-5 inline-flex items-center gap-3 bg-black px-8 py-4 text-[13px] font-bold uppercase tracking-[0.15em] text-white transition-colors hover:bg-gavra"
                >
                  <MapPin className="h-4 w-4" /> Dealer Locator
                </Link>
              </div>
            </Reveal>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-24">
            <h2 className="border-b border-black/10 pb-6 font-heading text-2xl font-extrabold uppercase tracking-tight md:text-4xl">
              More in {product.category}
            </h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
